import Loader from 'Components/Forms/Loader';
import SiteTable from 'FeatureModules/Site/Components/SiteTable/SiteTable';
import usePageTitle from 'hooks/usePageTitle';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useSite from 'FeatureModules/Site/hooks/useSite';
import useSiteActions from 'FeatureModules/Site/hooks/useSiteActions';
import useSites from 'FeatureModules/Site/hooks/useSites';
import React, { FC, useMemo } from 'react';
import { ESiteType, ESiteViewOption, IFarmSite } from 'FeatureModules/Site/types/site.types';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const ClusterSites: FC = () => {
  usePageTitle('Cluster sites');
  const { site, loading: siteLoading, error: siteError } = useSite({ skipPlotsQuery: true });
  const {
    sites,
    loading,
    error,
    orderBy,
    hasNextPage,
    summary,
    totalCount,
    searchTerm,
    handlePageEndReached,
    handleOrderByChange,
    handleSearchTermChange,
  } = useSites({ defaultViewOption: ESiteViewOption.LIST, siteClusterId: site?.id });
  const { handleCreateSite } = useSiteActions();

  const siteType = useMemo(() => {
    if (!sites?.length) return undefined;

    return sites[0].siteType;
  }, [sites]);

  const rawMaterial = useMemo(() => {
    if (!sites?.length) return undefined;

    return siteType === ESiteType.FARM ? (sites[0] as IFarmSite).rawMaterial : undefined;
  }, [sites, siteType]);

  if (error || siteError) return <ErrorState />;
  if (siteLoading) return <Loader />;

  return (
    <PageContainer>
      <PageTitle
        title="Cluster sites"
        goBackLabel="All sites"
        goBackUrl="/sites"
        titleExtension={site?.title}
      />

      <FlexBox my={2}>
        <ThemeButton
          size="medium"
          color="PRIMARY"
          onClick={() =>
            handleCreateSite({
              siteClusterId: site?.id,
              defaultSiteType: siteType,
              defaultRawMaterialId: rawMaterial?.id,
              defaultCountry: site?.locationName,
            })
          }
          startIcon={<Icon name="plus" color="WHITE" />}
        >
          Add site
        </ThemeButton>
      </FlexBox>

      <SiteTable
        maxHeight="calc(100vh - 280px)"
        // Only show the summary if the cluster sites are farms
        summary={
          site?.siteType === ESiteType.CLUSTER && site.sitesType === ESiteType.FARM
            ? summary
            : undefined
        }
        sites={sites}
        loading={loading}
        totalCount={totalCount}
        orderBy={orderBy}
        hasMoreRows={hasNextPage}
        searchTerm={searchTerm}
        onEndReached={handlePageEndReached}
        onOrderByChange={handleOrderByChange}
        onSearchTermChange={handleSearchTermChange}
      />
    </PageContainer>
  );
};

export default ClusterSites;
